/**
 * Date & Time Picker
 */
(() => {
  'use strict';

  const SELECTED_EVENT = 'datetime-picker:change';
  const DATE_EVENT = 'datetime-picker:date';
  const FIELD_SELECTOR = '.datetime-picker__field';
  const TIME_SELECTOR = '.datetime-picker__time';
  const STATE_ACTIVE = '_active';
  const STATE_SELECTED = '_selected';
  const STATE_TODAY = '_today';
  const STATE_DISABLED = '_disabled';
  const STATE_LOADING = '_loading';
  const STATE_CONFLICT = '_conflict';

  const CONFLICT_MESSAGE = 'Your selected time is no longer available for this update. Please choose another time.';

  const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const VIEW_DAYS = 'days';
  const VIEW_MONTHS = 'months';

  const DAY_FIRST_MIN = 0;
  const DAY_LAST_MIN = 23 * 60 + 50;
  const SLOT_STEP_MIN = 10;
  const SLOT_DURATION_HOURS = 1;
  const PAST_BUFFER_MIN = 10;
  const MINUTES_PER_HOUR = 60;

  /** @returns {Object|null} window.A9DateUtils once Day.js plugins are ready, else null. */
  function getDateUtils() {
    const utils = typeof window !== 'undefined' ? window.A9DateUtils : null;
    return utils && utils.hasTz && utils.hasTz() ? utils : null;
  }

  /**
   * Read-only view over an availability grid, resolved into a facility timezone.
   * @param {Array<{from:string,to:string,available:boolean}>} slots
   * @param {string} timeZone facility IANA timezone
   */
  class SlotAvailability {
    constructor(slots, timeZone) {
      this.timeZone = timeZone;
      this.dayMap = this._groupByLocalDay(Array.isArray(slots) ? slots : []);
    }

    /** @returns {Map<string, Array>} YYYY-MM-DD → sorted time entries (occupied ones disabled, not dropped). */
    _groupByLocalDay(slots) {
      const map = new Map();
      slots.forEach((slot) => {
        if (!slot || !slot.from) return;
        const start = dayjs.utc(slot.from).tz(this.timeZone);
        if (!start.isValid()) return;
        const ymd = start.format('YYYY-MM-DD');
        if (!map.has(ymd)) map.set(ymd, []);
        map.get(ymd).push({
          label: start.format('h:mm A'),
          from: slot.from,
          to: slot.to || null,
          disabled: slot.available !== true,
          sortKey: start.valueOf()
        });
      });
      map.forEach((times) => times.sort((a, b) => a.sortKey - b.sortKey));
      return map;
    }

    timesForDay(ymd) {
      return this.dayMap.get(ymd) || [];
    }
  }

  /**
   * Full-day time list for a facility-local day (12:00 AM … 11:50 PM, 10-min steps).
   * @param {string} ymd YYYY-MM-DD
   * @param {string} timeZone facility IANA timezone
   */
  function buildDayTimes(ymd, timeZone) {
    const utils = getDateUtils();
    if (!utils) return [];
    const base = dayjs.tz(ymd + ' 00:00:00', 'YYYY-MM-DD HH:mm:ss', timeZone);
    if (!base.isValid()) return [];

    const isToday = ymd === utils.currentYmd(timeZone);
    const nowMin = isToday ? utils.time12hToMinutes(utils.currentTime12h(timeZone)) : -1;

    const times = [];
    for (let mins = DAY_FIRST_MIN; mins <= DAY_LAST_MIN; mins += SLOT_STEP_MIN) {
      if (isToday && mins <= nowMin) continue; // past times are hidden
      const start = base.hour(Math.floor(mins / MINUTES_PER_HOUR)).minute(mins % MINUTES_PER_HOUR).second(0);
      if (!start.isValid()) continue;
      const end = start.add(SLOT_DURATION_HOURS, 'hour');
      times.push({
        label: start.format('h:mm A'),
        from: utils.toZuluFromDayjs(start),
        to: utils.toZuluFromDayjs(end),
        disabled: isToday && mins <= nowMin + PAST_BUFFER_MIN
      });
    }
    return times;
  }

  class DatetimePicker extends HTMLElement {
    constructor() {
      super();
      this.timeZone = this.dataset.timeZone || 'America/New_York';

      this.dateTrigger = this.querySelector('[data-datetime-date-trigger]');
      this.timeTrigger = this.querySelector('[data-datetime-time-trigger]');
      this.dateLabel = this.querySelector('[data-datetime-date-label]');
      this.timeLabel = this.querySelector('[data-datetime-time-label]');
      this.calendarPopover = this.querySelector('[data-datetime-calendar-popover]');
      this.timePopover = this.querySelector('[data-datetime-time-popover]');
      this.calendarMount = this.querySelector('[data-datetime-calendar]');
      this.timeList = this.querySelector('[data-datetime-timelist]');
      this.errorEl = this.querySelector('[data-datetime-error]');
      this.errorTextEl = this.querySelector('[data-datetime-error-text]');

      this.selectedYmd = null;
      this.selectedTime = null;

      this._hasGrid = false;
      this._loading = false;
      this._pendingTimeOpen = false;

      this.onDateTriggerClick = this.onDateTriggerClick.bind(this);
      this.onTimeTriggerClick = this.onTimeTriggerClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onCalendarClick = this.onCalendarClick.bind(this);
      this.onTimeListClick = this.onTimeListClick.bind(this);
    }

    connectedCallback() {
      const utils = getDateUtils();
      if (!this.dateTrigger || !this.calendarMount || !utils) return;

      this.availability = this._resolveAvailability();
      this.todayStart = utils.nowInZone(this.timeZone).startOf('day');
      this.currentMonth = this.todayStart.date(1);
      this.viewMonth = this.currentMonth;
      this.view = VIEW_DAYS;
      this.selectedYmd = this.todayStart.format('YYYY-MM-DD');
      this._renderCalendar();

      this.dateTrigger.addEventListener('click', this.onDateTriggerClick);
      this.timeTrigger.addEventListener('click', this.onTimeTriggerClick);
      this.calendarMount.addEventListener('click', this.onCalendarClick);
      this.timeList.addEventListener('click', this.onTimeListClick);
      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    _resolveAvailability() {
      let slots = [];
      const dataNode = this.querySelector('[data-datetime-slots]');
      if (dataNode) {
        try {
          slots = JSON.parse(dataNode.textContent || '[]');
        } catch (error) {
          slots = [];
        }
      }
      return new SlotAvailability(slots, this.timeZone);
    }

    /* ---- Calendar ------------------------------------------------------- */

    _renderCalendar() {
      if (this.view === VIEW_MONTHS) {
        this._renderMonthsView();
      } else {
        this._renderDaysView();
      }
    }

    _renderHeader(title, canGoPrev, prevLabel, nextLabel) {
      return '<div class="datetime-picker__cal-header">'
        + `<button type="button" class="datetime-picker__cal-nav datetime-picker__cal-nav--prev" data-datetime-cal-prev aria-label="${prevLabel}"${canGoPrev ? '' : ' disabled aria-disabled="true"'}></button>`
        + `<button type="button" class="datetime-picker__cal-title" data-datetime-cal-title aria-live="polite">${title}</button>`
        + `<button type="button" class="datetime-picker__cal-nav datetime-picker__cal-nav--next" data-datetime-cal-next aria-label="${nextLabel}"></button>`
        + '</div>';
    }

    _renderDaysView() {
      const canGoPrev = this.viewMonth.isAfter(this.currentMonth, 'month');
      const firstWeekday = this.viewMonth.day();
      const daysInMonth = this.viewMonth.daysInMonth();
      const todayYmd = this.todayStart.format('YYYY-MM-DD');

      const weekdays = WEEKDAYS
        .map((day) => `<span class="datetime-picker__cal-weekday">${day}</span>`)
        .join('');

      let cells = '';
      for (let blank = 0; blank < firstWeekday; blank += 1) {
        cells += '<span class="datetime-picker__cal-day datetime-picker__cal-day--blank" aria-hidden="true"></span>';
      }
      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = this.viewMonth.date(day);
        const ymd = date.format('YYYY-MM-DD');
        const isDisabled = date.isBefore(this.todayStart, 'day');
        const isSelected = ymd === this.selectedYmd;
        const stateClass = [
          ymd === todayYmd ? STATE_TODAY : '',
          isSelected ? STATE_SELECTED : '',
          isDisabled ? STATE_DISABLED : ''
        ].filter(Boolean).join(' ');

        cells += `<button type="button" class="datetime-picker__cal-day${stateClass ? ' ' + stateClass : ''}"`
          + ` data-datetime-day="${ymd}"`
          + ` aria-label="${date.format('dddd, MMMM D, YYYY')}"`
          + ` aria-pressed="${isSelected ? 'true' : 'false'}"`
          + `${isDisabled ? ' disabled aria-disabled="true"' : ''}>${day}</button>`;
      }

      this.calendarMount.innerHTML =
        this._renderHeader(this.viewMonth.format('MMMM YYYY'), canGoPrev, 'Previous month', 'Next month')
        + `<div class="datetime-picker__cal-weekdays">${weekdays}</div>`
        + `<div class="datetime-picker__cal-grid">${cells}</div>`;
    }

    _renderMonthsView() {
      const canGoPrev = this.viewMonth.year() > this.currentMonth.year();

      let cells = '';
      for (let month = 0; month < MONTHS.length; month += 1) {
        const monthStart = this.viewMonth.month(month).date(1);
        const isDisabled = monthStart.isBefore(this.currentMonth, 'month');
        const stateClass = [
          monthStart.isSame(this.currentMonth, 'month') ? STATE_TODAY : '',
          monthStart.isSame(this.viewMonth, 'month') ? STATE_SELECTED : '',
          isDisabled ? STATE_DISABLED : ''
        ].filter(Boolean).join(' ');

        cells += `<button type="button" class="datetime-picker__cal-month${stateClass ? ' ' + stateClass : ''}"`
          + ` data-datetime-month="${month}"`
          + ` aria-label="${monthStart.format('MMMM YYYY')}"`
          + `${isDisabled ? ' disabled aria-disabled="true"' : ''}>${MONTHS[month]}</button>`;
      }

      this.calendarMount.innerHTML =
        this._renderHeader(this.viewMonth.format('YYYY'), canGoPrev, 'Previous year', 'Next year')
        + `<div class="datetime-picker__cal-months">${cells}</div>`;
    }

    onCalendarClick(event) {
      event.stopPropagation();

      const prev = event.target.closest('[data-datetime-cal-prev]');
      const next = event.target.closest('[data-datetime-cal-next]');
      const title = event.target.closest('[data-datetime-cal-title]');
      const month = event.target.closest('[data-datetime-month]');
      const day = event.target.closest('[data-datetime-day]');
      const step = this.view === VIEW_MONTHS ? 'year' : 'month';

      if (prev && !prev.disabled) {
        this.viewMonth = this.viewMonth.subtract(1, step);
        this._renderCalendar();
      } else if (next) {
        this.viewMonth = this.viewMonth.add(1, step);
        this._renderCalendar();
      } else if (title) {
        this.view = this.view === VIEW_MONTHS ? VIEW_DAYS : VIEW_MONTHS;
        this._renderCalendar();
      } else if (month && !month.disabled) {
        this.viewMonth = this.viewMonth.month(Number(month.dataset.datetimeMonth)).date(1);
        this.view = VIEW_DAYS;
        this._renderCalendar();
      } else if (day && !day.disabled) {
        this._selectDay(day.dataset.datetimeDay);
      }
    }

    _selectDay(ymd) {
      this.selectedYmd = ymd;
      this.dateLabel.textContent = dayjs(ymd, 'YYYY-MM-DD').format('ddd, MMMM D');
      this.dateTrigger.closest(FIELD_SELECTOR).classList.add(STATE_SELECTED);
      this._renderCalendar();

      this._resetTimeSelection();
      this.flagSelectionConflict(false);
      this._renderTimes(ymd);

      this.dispatchEvent(new CustomEvent(DATE_EVENT, {
        bubbles: true,
        detail: { ymd, timeZone: this.timeZone }
      }));

      this.closePopovers();
      this._pendingTimeOpen = true;
      this._maybeOpenTimePopover();
    }

    _maybeOpenTimePopover() {
      if (!this._pendingTimeOpen || this._loading || this.timeTrigger.disabled) return;
      this._pendingTimeOpen = false;
      const timeField = this.timeTrigger.closest(FIELD_SELECTOR);
      this.openPopover(this.timePopover, this.timeTrigger, timeField);
    }

    /* ---- Times ---------------------------------------------------------- */

    _resetTimeSelection() {
      this.selectedTime = null;
      this.timeLabel.textContent = 'Select Time';
      this.timeTrigger.closest(FIELD_SELECTOR).classList.remove(STATE_SELECTED);
    }

    _timesForDay(ymd) {
      const times = this.availability.timesForDay(ymd);
      if (times.length || this._hasGrid) return times;
      return buildDayTimes(ymd, this.timeZone);
    }

    _renderTimes(ymd) {
      if (this._loading) {
        this._renderTimeMessage('Loading times…');
        this.timeTrigger.disabled = true;
        return;
      }

      const times = this._timesForDay(ymd);

      if (!times.length) {
        this._renderTimeMessage('No times available for this day');
        this.timeTrigger.disabled = true;
        return;
      }

      this.timeTrigger.disabled = false;
      this.timeList.innerHTML = '';

      times.forEach((time) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = `datetime-picker__time${time.disabled ? ' ' + STATE_DISABLED : ''}`;
        option.setAttribute('role', 'option');
        option.textContent = time.label;
        option.dataset.from = time.from;
        if (time.to) option.dataset.to = time.to;
        if (time.disabled) {
          option.disabled = true;
          option.setAttribute('aria-disabled', 'true');
        }
        const isSelected = !time.disabled && time.label === this.selectedTime;
        option.classList.toggle(STATE_SELECTED, isSelected);
        option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        this.timeList.appendChild(option);
      });
    }

    _renderTimeMessage(text) {
      this.timeList.innerHTML = '';
      const message = document.createElement('p');
      message.className = 'datetime-picker__time-empty font-text-sm-regular text-color-secondary';
      message.textContent = text;
      this.timeList.appendChild(message);
    }

    _isTimeOptionBookable(option) {
      if (!option || !this.selectedYmd) return false;
      const from = option.dataset.from;
      const label = option.textContent;
      return this._timesForDay(this.selectedYmd)
        .some((time) => !time.disabled && time.from === from && time.label === label);
    }

    onTimeListClick(event) {
      const option = event.target.closest(TIME_SELECTOR);
      if (!option || option.disabled || !this._isTimeOptionBookable(option)) return;

      this.timeList.querySelectorAll(TIME_SELECTOR).forEach((node) => {
        node.classList.remove(STATE_SELECTED);
        node.setAttribute('aria-selected', 'false');
      });
      option.classList.add(STATE_SELECTED);
      option.setAttribute('aria-selected', 'true');

      this.selectedTime = option.textContent;
      this.timeLabel.textContent = option.textContent;
      this.timeTrigger.closest(FIELD_SELECTOR).classList.add(STATE_SELECTED);
      this.closePopovers();

      this.flagSelectionConflict(false);

      this.dispatchEvent(new CustomEvent(SELECTED_EVENT, {
        bubbles: true,
        detail: {
          ymd: this.selectedYmd,
          time: this.selectedTime,
          from: option.dataset.from,
          to: option.dataset.to || null,
          timeZone: this.timeZone
        }
      }));
    }

    /* ---- Public API (driven by a booking controller) ------------------- */

    setAvailableSlots(slots) {
      this._hasGrid = true;
      this.setLoading(false);
      this.availability = new SlotAvailability(Array.isArray(slots) ? slots : [], this.timeZone);
      if (this.selectedYmd) this._renderTimes(this.selectedYmd);
      this._maybeOpenTimePopover();
    }

    setLoading(isLoading) {
      this._loading = !!isLoading;
      this.timeTrigger.closest(FIELD_SELECTOR)?.classList.toggle(STATE_LOADING, this._loading);
      if (this._loading && this.selectedYmd) this._renderTimes(this.selectedYmd);
    }

    /** @returns {{ymd,time,from,to,timeZone}|null} current selection. */
    getSelection() {
      if (!this.selectedYmd || !this.selectedTime) return null;
      const option = Array.from(this.timeList.querySelectorAll(TIME_SELECTOR))
        .find((node) => node.textContent === this.selectedTime);
      return {
        ymd: this.selectedYmd,
        time: this.selectedTime,
        from: option?.dataset.from || null,
        to: option?.dataset.to || null,
        timeZone: this.timeZone
      };
    }

    clearSelection() {
      this._resetTimeSelection();
      this.flagSelectionConflict(false);
      if (this.selectedYmd) this._renderTimes(this.selectedYmd);
    }

    reset() {
      this.selectedYmd = null;
      this._hasGrid = false;
      this._pendingTimeOpen = false;
      this.availability = new SlotAvailability([], this.timeZone);

      this._resetTimeSelection();
      this.flagSelectionConflict(false);
      this.setLoading(false);

      this.dateLabel.textContent = 'Select Date';
      this.dateTrigger.closest(FIELD_SELECTOR)?.classList.remove(STATE_SELECTED);
      this.timeTrigger.disabled = true;

      this.closePopovers();
      this._renderCalendar();
    }

    flagSelectionConflict(hasConflict) {
      this.timeTrigger.closest(FIELD_SELECTOR)?.classList.toggle(STATE_CONFLICT, !!hasConflict);
      this.timeTrigger.setAttribute('aria-invalid', hasConflict ? 'true' : 'false');
      if (this.errorEl) {
        if (this.errorTextEl) this.errorTextEl.textContent = hasConflict ? CONFLICT_MESSAGE : '';
        this.errorEl.hidden = !hasConflict;
      }
    }

    /* ---- Popovers ------------------------------------------------------- */

    openPopover(popover, trigger, field) {
      this.closePopovers();
      popover.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      field.classList.add(STATE_ACTIVE);
    }

    closePopovers() {
      [
        { popover: this.calendarPopover, trigger: this.dateTrigger },
        { popover: this.timePopover, trigger: this.timeTrigger }
      ].forEach(({ popover, trigger }) => {
        if (!popover) return;
        popover.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        trigger.closest(FIELD_SELECTOR)?.classList.remove(STATE_ACTIVE);
      });
    }

    onDateTriggerClick(event) {
      event.stopPropagation();
      const field = this.dateTrigger.closest(FIELD_SELECTOR);
      if (this.calendarPopover.hidden) {
        this.openPopover(this.calendarPopover, this.dateTrigger, field);
      } else {
        this.closePopovers();
      }
    }

    onTimeTriggerClick(event) {
      event.stopPropagation();
      if (this.timeTrigger.disabled) return;
      const field = this.timeTrigger.closest(FIELD_SELECTOR);
      if (this.timePopover.hidden) {
        this.openPopover(this.timePopover, this.timeTrigger, field);
      } else {
        this.closePopovers();
      }
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.closePopovers();
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      const wasOpen = !this.calendarPopover.hidden || !this.timePopover.hidden;
      this.closePopovers();
      if (wasOpen) this.dateTrigger.focus();
    }
  }

  customElements.define('datetime-picker', DatetimePicker);
})();
